import React, { useMemo } from 'react';
import { Box } from '@ublend-npm/aulaui-core';
import { useSelector } from 'react-redux';
import { selectors as reactionSelectors } from '@app/store/reactions';
import { UB_CLASS_ROOM_POST_ITEM_TYPE } from '@app/utils/itemTypeForAnalytics';
import type { AulaState } from '@core/types/state';
import {
  ReactionList,
  ReactionPicker,
} from '@components/presentational/ResponsiveReactions';

type PostReactionsProps = {
  postId: string;
  readOnly?: boolean;
};

type GetReactionsSelector = (
  state: AulaState,
  props: { itemId: string }
) => unknown[];

const PostReactions = ({ postId, readOnly = false }: PostReactionsProps) => {
  const getReactions = useMemo<GetReactionsSelector>(() => {
    const selector: any = reactionSelectors.getReactionsSelector();
    return (state, props) => selector(state, props);
  }, []);
  const hasReactions = useSelector<AulaState, boolean>(
    (state) => getReactions(state, { itemId: postId }).length > 0
  );

  // Staff access has no reaction picker. Keep the section only when there is
  // an existing reaction to display, matching the current Common Room feed.
  if (readOnly && !hasReactions) return null;

  return (
    <Box
      aria-label="Post reactions"
      sx={{
        alignItems: 'center',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        minHeight: '40px',
        minWidth: 0,
        width: '100%',
      }}
    >
      <ReactionList
        itemId={postId}
        itemType={UB_CLASS_ROOM_POST_ITEM_TYPE}
        readOnly={readOnly}
        hideWhenNoReactions={readOnly}
      />
      <ReactionPicker
        itemId={postId}
        itemType={UB_CLASS_ROOM_POST_ITEM_TYPE}
        readOnly={readOnly}
      />
    </Box>
  );
};

export default PostReactions;
